/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  NumberField,
  SelectInput,
  TextField,
  useList,
  useNotify,
} from "react-admin";

import {
  AddAllIcon,
  CancelIcon,
  CloseIcon,
  DAMAGED_COLOR,
} from "../../../components/common/iconography";
import { Pagination } from "../../../components/Pagination";
import { SearchField } from "../../../components/common/SearchField";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { CustomField } from "../../../components/common/CustomFields";
import SwitchOnOff from "../../../components/common/AntSwitch";
import { ageRenderer } from "../../../components/common/AgeRenderer";

import {
  AreaType,
  LotType,
  LotTypes,
  SORTKEY_BUFFER,
  ShipmentLineItemStatus,
} from "../../../utils/constants";
import { logError } from "../../../utils/appUtils";
import {
  excludeDamagedInventories,
  getVisibleInventoryItems,
} from "../../../utils/damagedInventory";
import {
  availablePiecesRenderer,
  exceptionRenderer,
  piecesRenderer,
  quantityRenderer,
  totalPiecesRenderer,
  trimDecimal,
} from "../../../utils/utils";
import { sortByLocation } from "../../../utils/locationUtils";
import { useListWithSecondarySort } from "../../../utils/useListWithSecondarySort";
import {
  calculatePickListQuantityState,
  calculatePickListRowQuantityState,
  mergePickListRows,
} from "./lineItem/PickListItemUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 1001,
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(8),
  },
  filter: {
    display: "flex",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "3px",
    paddingBottom: "0px",
    justifyContent: "space-between",
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "100vh",
  },
  textRight: {
    textAlign: "right",
  },
  textLeft: {
    flexGrow: 1, // Allows the text to take up space pushing the buttons to the right
    [theme.breakpoints.down("sm")]: {
      width: "100%", // Full width on small screens to ensure the text and buttons stack
      marginBottom: theme.spacing(1), // Adds a margin below the text on small screens
    },
  },
  actionButtons: {
    [theme.breakpoints.down("sm")]: {
      width: "100%", // Full width on small screens to ensure the buttons stack below the text
      display: "flex",
      justifyContent: "flex-end", // Aligns buttons to the right on small screens
    },
  },
}));

const getTotalWeight = (record) => {
  // Return null if no record is provided
  if (!record) return null;

  // Determine the weight, preferring record.weight over record.inventory?.weight
  const weight = record.weight ?? record.inventory?.weight;

  // Return null if weight is null, undefined, or an empty string
  if (weight == null || weight === "") return null;

  // Check if quantityOnHand is provided and is not null or undefined
  const quantityOnHand = record.quantityOnHand ?? null;

  // If quantityOnHand is null, undefined, or an empty string, return null
  if (quantityOnHand == null) return null;

  // Calculate total weight if both weight and quantityOnHand are numbers (including zero)
  if (!isNaN(weight) && !isNaN(quantityOnHand)) {
    return weight * quantityOnHand;
  }

  // Return null if any condition for a valid calculation is not met
  return null;
};

const ListFilter = ({
  allInventories,
  setInventories,
  searchLot,
  setSearchLot,
  searchAreaId,
  setSearchAreaId,
  byLocation,
  defaultSort,
  currentSort,
  setSort,
  isContainerPickUp,
  ...props
}) => {
  const classes = useStyles();
  const [search, setSearch] = useState();
  const [filterValues, setFilters] = useState({
    lotType: searchLot ? searchLot : "",
    searchArea: searchAreaId ? searchAreaId : "",
  });

  useEffect(() => {
    handleInventorySearch(search);
  }, [search, searchLot, searchAreaId]);

  const handleInventorySearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const filteredInventories = allInventories.filter(
      (inv) =>
        (inv.productCode?.toLowerCase().includes(searchTerm) ||
          inv.productName?.toLowerCase().includes(searchTerm) ||
          inv.lotNumber?.toLowerCase().includes(searchTerm) ||
          inv.containerNumber?.toLowerCase().includes(searchTerm) ||
          inv.poNumber?.toLowerCase().includes(searchTerm) ||
          inv.railCarNumber?.toLowerCase().includes(searchTerm) ||
          inv.customFields?.toString()?.toLowerCase().includes(searchTerm) ||
          inv.inventoryItems.items.some((invItm) =>
            invItm.locationFullName?.toLowerCase().includes(searchTerm),
          )) &&
        (searchLot ? inv.lotType === searchLot : true) &&
        (searchAreaId
          ? inv.inventoryItems.items.some(
              (invItm) => invItm.areaId === searchAreaId,
            )
          : true),
    );

    setInventories(filteredInventories);
  };

  return (
    <Filter
      {...props}
      variant="outlined"
      className={classes.filter}
      filterValues={filterValues}
      setFilters={setFilters}
    >
      <SearchField
        label="Inventory Search"
        margin="dense"
        variant="outlined"
        alwaysOn
        handleSearch={setSearch}
        searchOnChange={true}
      />
      <SelectInput
        source="lotType"
        label="Lot Type"
        variant="outlined"
        alwaysOn
        choices={LotTypes}
        allowEmpty
        resettable
        onChange={(event) => setSearchLot(event?.target?.value)}
        helperText={false}
      />
      <SelectInput
        source="searchArea"
        label="Area"
        variant="outlined"
        alwaysOn
        choices={
          isContainerPickUp
            ? window.appUser.reloadYard.areas.items.filter(
                (item) => item.areaType === AreaType.BULK,
              )
            : window.appUser.reloadYard.areas.items
        }
        allowEmpty
        resettable
        onChange={(event) => setSearchAreaId(event?.target?.value)}
        helperText={false}
      />
    </Filter>
  );
};

const processInventories = (
  allInventories,
  setInventories,
  setAllInventoriesMap,
  notify,
) => {
  try {
    let inventoryData = [...allInventories];
    const inventoryMap = inventoryData.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
    setAllInventoriesMap(inventoryMap);
    setInventories(inventoryData);
  } catch (error) {
    logError(
      "outboundShipment:InventorySearchDialog:processInventories",
      error,
      notify,
    );
  }
};

const hydrateLocationForSort = (inventoryItem) => {
  const location =
    inventoryItem.location ||
    window.appUser?.reloadYard?.locationMap?.[inventoryItem.locationId];
  if (!location) return inventoryItem;

  const area =
    location.area ||
    inventoryItem.area ||
    window.appUser?.reloadYard?.areaMap?.[
      location.areaId || inventoryItem.areaId
    ];
  const zone =
    location.zone ||
    inventoryItem.zone ||
    window.appUser?.reloadYard?.zoneMap?.[
      location.zoneId || inventoryItem.zoneId
    ];
  const aisle =
    location.aisle ||
    inventoryItem.aisle ||
    window.appUser?.reloadYard?.aisleMap?.[
      location.aisleId || inventoryItem.aisleId
    ];

  return {
    ...inventoryItem,
    location: {
      ...location,
      area,
      zone,
      aisle,
    },
  };
};

const getDialogInventories = (
  allInventories,
  initialPickListItem,
  pickListItem,
) => {
  if (!initialPickListItem?.inventoryItems?.items?.length) {
    return allInventories;
  }

  const quantityState = calculatePickListQuantityState(
    initialPickListItem,
    pickListItem,
  );
  const currentInventoryItems = pickListItem?.inventoryItems?.items || [];
  const currentInventoryItemIds = new Set(
    currentInventoryItems
      .map((inventoryItem) => inventoryItem?.inventoryItemId)
      .filter(Boolean),
  );
  const removedInitialInventoryItems = initialPickListItem.inventoryItems.items
    .filter(
      (inventoryItem) =>
        inventoryItem?.inventoryItemId &&
        !currentInventoryItemIds.has(inventoryItem.inventoryItemId) &&
        inventoryItem?.inventoryItem,
    )
    .filter((inventoryItem) => {
      const rowQuantityState = calculatePickListRowQuantityState(
        initialPickListItem,
        pickListItem,
        { ...inventoryItem, quantityToPick: 0 },
      );
      return rowQuantityState.maxQuantityToPick > 0;
    });
  const currentInventoryItemsWithAvailability = currentInventoryItems.filter(
    (inventoryItem) => {
      if (!inventoryItem?.inventoryItemId || !inventoryItem?.inventoryItem) {
        return false;
      }
      const rowQuantityState = calculatePickListRowQuantityState(
        initialPickListItem,
        pickListItem,
        inventoryItem,
      );
      return rowQuantityState.quantityAvailable > 0;
    },
  );

  const inventoriesById = allInventories.reduce((acc, inventory) => {
    acc[inventory.id] = {
      ...inventory,
      inventoryItems: {
        ...inventory.inventoryItems,
        items: [...(inventory.inventoryItems?.items || [])],
      },
    };
    return acc;
  }, {});

  [
    ...removedInitialInventoryItems,
    ...currentInventoryItemsWithAvailability,
  ].forEach((pickListInventoryItem) => {
    const inventoryId =
      pickListInventoryItem.inventoryId ||
      pickListItem.inventoryId ||
      initialPickListItem.inventoryId;
    const inventory =
      inventoriesById[inventoryId] ||
      (pickListItem.inventory || initialPickListItem.inventory
        ? {
            ...(pickListItem.inventory || initialPickListItem.inventory),
            inventoryItems: {
              ...((pickListItem.inventory || initialPickListItem.inventory)
                .inventoryItems || {}),
              items: [],
            },
          }
        : null);

    if (!inventory) return;

    const rowQuantityState = calculatePickListRowQuantityState(
      initialPickListItem,
      pickListItem,
      currentInventoryItemIds.has(pickListInventoryItem.inventoryItemId)
        ? pickListInventoryItem
        : { ...pickListInventoryItem, quantityToPick: 0 },
    );
    const alreadyPresent = inventory.inventoryItems.items.some(
      (inventoryItem) =>
        inventoryItem.id === pickListInventoryItem.inventoryItemId,
    );
    if (!alreadyPresent) {
      inventory.inventoryItems.items.push({
        ...hydrateLocationForSort(pickListInventoryItem.inventoryItem),
        quantityCommitted: rowQuantityState.quantityCommitted,
      });
    }
    inventoriesById[inventoryId] = inventory;
  });

  Object.values(inventoriesById).forEach((inventory) => {
    inventory.inventoryItems.items = inventory.inventoryItems.items.map(
      (inventoryItem) => {
        const inventoryItemState =
          quantityState.inventoryItemStates[inventoryItem.id];
        if (!inventoryItemState) return inventoryItem;

        return {
          ...inventoryItem,
          quantityCommitted: inventoryItemState.quantityCommitted,
        };
      },
    );
  });

  return Object.values(inventoriesById);
};

/**
 * The inventoryItem ids already committed to the pick list. A damaged item can
 * legitimately be on there — it was picked deliberately on an earlier pass —
 * so it must survive the damage filter. The dialog reflects the pick list on
 * reopen, it does not re-litigate it.
 */
const getPickListInventoryItemIds = (form, pickListItem) => {
  const lineItems = pickListItem
    ? [pickListItem]
    : form?.getFieldState("shipmentLineItems.items")?.value || [];

  return lineItems.reduce((acc, lineItem) => {
    lineItem?.inventoryItems?.items?.forEach((inventoryItem) => {
      if (inventoryItem.inventoryItemId) acc.add(inventoryItem.inventoryItemId);
    });
    return acc;
  }, new Set());
};

const ListSwitch = ({
  byLocation,
  setByLocation,
  includeDamaged,
  setIncludeDamaged,
  isContainerPickUp,
  ...props
}) => {
  return (
    <Box mt={1.5} display="flex" alignItems="center">
      <SwitchOnOff
        leftLabel="By inventory"
        rightLabel="By location"
        disabled={isContainerPickUp}
        checked={byLocation}
        setChecked={() => setByLocation(!byLocation)}
      />
      <Box ml={3}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(includeDamaged)}
              onChange={(event) => setIncludeDamaged(event.target.checked)}
              style={includeDamaged ? { color: DAMAGED_COLOR } : null}
            />
          }
          label={<Typography variant="body2">Show damaged</Typography>}
        />
      </Box>
    </Box>
  );
};

// InventorySearchDialog
// ├── inventoryListContext: per-inventory
// │
// ├── InventoryGrid: "By Inventory" view
// │   ├── expand
// │   └── InventoryItemPeak
// │     └── ListContext: per-inventoryItem
// │
// └── "By Location" view
//     └── LocationGrid
//         └── NO expand
//         └── ListContext: per-inventorItem
export const InventorySearchDialog = ({
  form,
  pickListItem,
  initialPickListItem,
  account,
  allInventories,
  isOpen,
  setIsOpen,
  isLoaded,
  updateShipment,
  isContainerPickUp,
  ...props
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [allInventoriesMap, setAllInventoriesMap] = useState({});
  const [searchLot, setSearchLot] = useState(
    account?.lotType ? account.lotType : "",
  );
  const [searchAreaId, setSearchAreaId] = useState("");
  const [selectedInventoryIds, setSelectedInventoryIds] = useState(new Set());
  const [selectedInventoryItemIds, setSelectedInventoryItemIds] = useState(
    new Set(),
  );
  const [total, setTotal] = useState("");
  const [byLocation, setByLocation] = useState(false);
  const [includeDamaged, setIncludeDamaged] = useState(false);
  const defaultSort = { field: "productName", order: "ASC" };
  const secondarySort =
    searchLot === LotType.PERPETUAL
      ? { field: "receivedAt", order: "ASC" }
      : searchLot === LotType.CARLOT
        ? { field: "lotNumber", order: "ASC" }
        : null;

  var formatter = new Intl.NumberFormat("en-US");

  // Memoize inventoryItemMap
  const dialogInventories = useMemo(
    () =>
      getDialogInventories(allInventories, initialPickListItem, pickListItem),
    [allInventories, initialPickListItem, pickListItem],
  );
  const allInventoryItemMap = useMemo(
    () =>
      dialogInventories.reduce((acc, inventory) => {
        inventory.inventoryItems.items.forEach((item) => {
          acc[item.id] = { ...item };
        });
        return acc;
      }, {}),
    [dialogInventories],
  );

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = sessionStorage.getItem(
    `InventorySearchDialog_perPage`,
  );
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  useEffect(() => {
    if (account?.id) {
      setSearchLot(account?.lotType ? account.lotType : "");
    }

    if (isContainerPickUp) {
      setByLocation(true);
      const bulkArea = window.appUser?.reloadYard?.areas?.items?.find(
        (item) => item.areaType === AreaType.BULK,
      );
      setSearchAreaId(bulkArea?.id || "");
    } else {
      setSearchAreaId("");
    }
  }, [isContainerPickUp, account]);

  useEffect(() => {
    if (dialogInventories?.length > 0 && isOpen) {
      processInventories(
        dialogInventories,
        setInventories,
        setAllInventoriesMap,
        notify,
      );
    }
  }, [dialogInventories, isOpen]);

  useEffect(() => {
    // On open, Remove selectedIds if pickList was reset (account change, ISOtainer pickup change)
    if (isOpen) {
      // "Show damaged" is a per-pick override, not a preference. On the outbound
      // forms this dialog is rendered unconditionally and never unmounts, so
      // without this the checkbox stays ticked for the rest of the page's life:
      // the next opener -- possibly after an account change that reset the pick
      // list -- would find damaged rows visible and selectable with nothing on
      // screen saying the guard was off.
      setIncludeDamaged(false);

      // shipmentLineItems.items only appllies in context of an outbound, not PickListItemEdit
      let picklistState = form.getFieldState("shipmentLineItems.items");

      if (picklistState?.value?.length > 0) {
        const currentInventoryIds = new Set();
        const currentInventoryItemIds = new Set();

        picklistState.value.forEach((lineItem) => {
          const mergeKey = lineItem.inventoryId ?? lineItem.inventory?.id;
          if (mergeKey) {
            currentInventoryIds.add(mergeKey);
          }

          lineItem.inventoryItems?.items?.forEach((inventoryItem) => {
            if (inventoryItem.inventoryItemId) {
              currentInventoryItemIds.add(inventoryItem.inventoryItemId);
            }
          });
        });

        // Filter only still-existing inventory IDs
        setSelectedInventoryIds((prev) => {
          return new Set([...prev].filter((id) => currentInventoryIds.has(id)));
        });

        // Filter only still-existing inventoryItem IDs
        setSelectedInventoryItemIds((prev) => {
          return new Set(
            [...prev].filter((id) => currentInventoryItemIds.has(id)),
          );
        });
      } else {
        // No line items — clear selections
        setSelectedInventoryIds(new Set());
        setSelectedInventoryItemIds(new Set());
      }
    }
  }, [isOpen]);

  useEffect(() => {
    try {
      if (selectedInventoryItemIds.size > 0) {
        const qtyMap = {};
        const weightMap = {};
        let totalString = "";

        selectedInventoryItemIds.forEach((selectedId) => {
          const inventoryItem = allInventoryItemMap[selectedId];
          const uom = inventoryItem.inventory.inventoryUOM;
          const quantity = inventoryItem.quantityOnHand;
          const wuom = inventoryItem.inventory.weightUOM;
          const weight = inventoryItem.inventory.weight || 0;

          qtyMap[uom] = (qtyMap[uom] || 0) + quantity;
          if (wuom) {
            weightMap[wuom] = (weightMap[wuom] || 0) + quantity * weight;
          }
        });

        totalString = "Selected";
        for (const [key, value] of Object.entries(qtyMap)) {
          totalString =
            totalString + " " + key + ": " + formatter.format(value);
        }
        for (const [key, value] of Object.entries(weightMap)) {
          totalString =
            totalString + " " + key + ": " + formatter.format(value);
        }

        setTotal(totalString);
      }
    } catch (error) {
      logError(
        "outboundShipments:InventorySearchDialog:calcTotalSelected",
        error,
        notify,
      );
    }
  }, [selectedInventoryItemIds]);

  // Turning "Show damaged" back off must drop the damaged rows the user ticked
  // while it was on. Hiding a row does not deselect it, and handleAdd walks the
  // unfiltered item list, so a stale id would put damaged stock on the shipment
  // with nothing on screen to account for it. Items already on the pick list
  // are left alone — they are hidden but deliberately selected, and unselecting
  // their parent row still clears them.
  useEffect(() => {
    if (includeDamaged) return;

    const pickListInventoryItemIds = getPickListInventoryItemIds(
      form,
      pickListItem,
    );
    const droppedInventoryItemIds = [...selectedInventoryItemIds].filter(
      (id) =>
        allInventoryItemMap[id]?.isDamaged && !pickListInventoryItemIds.has(id),
    );
    if (droppedInventoryItemIds.length === 0) return;

    const keptInventoryItemIds = new Set(selectedInventoryItemIds);
    droppedInventoryItemIds.forEach((id) => keptInventoryItemIds.delete(id));
    setSelectedInventoryItemIds(keptInventoryItemIds);

    // Re-check only the inventories that lost an item: one left with nothing
    // selected is no longer selected either, or handleAdd would build an empty
    // line item for it. Inventories untouched by the prune keep their state,
    // including a selected inventory that has no items placed yet.
    const touchedInventoryIds = new Set(
      droppedInventoryItemIds.map((id) => allInventoryItemMap[id].inventoryId),
    );
    const keptInventoryIds = new Set(
      [...selectedInventoryIds].filter((inventoryId) => {
        if (!touchedInventoryIds.has(inventoryId)) return true;
        const items = allInventoriesMap[inventoryId]?.inventoryItems?.items;
        return Boolean(
          items?.some((item) => keptInventoryItemIds.has(item.id)),
        );
      }),
    );
    if (keptInventoryIds.size !== selectedInventoryIds.size) {
      setSelectedInventoryIds(keptInventoryIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDamaged]);

  // Applied here rather than in ListFilter so the damage filter also holds on
  // the pickListItem path, which renders no filter toolbar at all.
  const visibleInventories = useMemo(
    () => excludeDamagedInventories(inventories, includeDamaged),
    [inventories, includeDamaged],
  );

  // The list hook re-sorts and re-slices whenever its ids array changes
  // identity, so an ids array built inline would do that work on every render
  // of the dialog -- every keystroke in the search box included.
  const visibleInventoryIds = useMemo(
    () => visibleInventories.map((i) => i.id),
    [visibleInventories],
  );

  const inventoryListContext = useListWithSecondarySort({
    data: visibleInventories,
    ids: visibleInventoryIds,
    loaded: isLoaded,
    loading: !isLoaded,
    sort: defaultSort,
    secondarySort: secondarySort,
    perPage: defaultPerPage,
    basePath: "/inventories",
    resource: "inventories",
    notify,
  });

  const handleAdd = () => {
    setSaving(true);
    try {
      let shipmentItems = [];
      const newShipmentItems = [];

      if (pickListItem) {
        // adding more inventoryItems to a shipmentLineItem
        shipmentItems = [pickListItem];
      } else {
        const shipmentItemsState = form.getFieldState(
          "shipmentLineItems.items",
        );
        shipmentItems = shipmentItemsState?.value
          ? shipmentItemsState.value
          : [];
      }

      selectedInventoryIds.forEach((inventoryId) => {
        const inventory = allInventoriesMap[inventoryId];
        const newLineItemInventoryItems = [];

        inventory.inventoryItems.items.forEach((inventoryItem) => {
          // check if inventoryItem selected by inventoryItem ID
          if (selectedInventoryItemIds.has(inventoryItem.id)) {
            const newLineItemInventoryItem = {
              sliiiUuid: uuidv4(), // persistent ShipmentLineItemInventoryItem id
              status: ShipmentLineItemStatus.NEW,
              groups: inventory.groups,
              reloadYardId: inventory.reloadYardId,
              outboundShipmentId: null,
              shipmentLineItemId: null,
              inventoryItemId: inventoryItem.id,
              inventoryItem: inventoryItem,
              quantityToPick: 0,
            };
            const rowQuantityState = calculatePickListRowQuantityState(
              initialPickListItem || pickListItem,
              pickListItem,
              newLineItemInventoryItem,
            );
            newLineItemInventoryItems.push({
              ...newLineItemInventoryItem,
              quantityToPick: rowQuantityState.maxQuantityToPick,
            });
          }
        });

        const newPickListItem = {
          id: inventory.id, // temporary id for listContext to work. Delete before save.
          lineItemUuid: uuidv4(), // persistent ShipmentLineItem id, survives re-save
          active: "true",
          status: ShipmentLineItemStatus.NEW,
          groups: inventory.groups,
          productName: inventory.productName,
          lotNumber: inventory.lotNumber,
          reloadYardId: inventory.reloadYardId,
          inventoryId: inventory.id,
          inventory: inventory,
          inventoryItems: { items: newLineItemInventoryItems },
          totalQuantityToPick: trimDecimal(
            newLineItemInventoryItems.reduce(
              (total, sliii) => total + sliii.quantityToPick,
              0,
            ),
            3,
          ),
        };

        newShipmentItems.push(newPickListItem);
      });

      if (newShipmentItems?.length > 0) {
        const unionShipmentItems = mergePickListRows(
          shipmentItems,
          newShipmentItems,
          { isLineItemEdit: Boolean(pickListItem), notify },
        );
        if (!pickListItem) {
          // do not change sortKey on existing shipmentLineItem
          unionShipmentItems.forEach(
            (itm, idx) => (itm.sortKey = idx + SORTKEY_BUFFER),
          );
        }
        updateShipment && updateShipment(unionShipmentItems);
      }

      // Optional: Clear selections after adding
      // setSelectedInventoryIds(new Set());
      // setSelectedInventoryItemIds(new Set());
    } catch (error) {
      logError(
        "outboundShipment:InventorySearchDialog:handleAdd",
        error,
        notify,
      );
    } finally {
      handleClose();
      setSaving(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return; // ignore backdrop clicks
    }
    setIsOpen(false);
    inventoryListContext.onUnselectItems();
  };

  const commonProps = {
    ...props,
    account,
    allInventoriesMap,
    allInventoryItemMap,
    byLocation,
    setByLocation: isContainerPickUp ? () => null : setByLocation,
    includeDamaged,
    setIncludeDamaged,
    isContainerPickUp,
    setDefaultPerPage,
    selectedInventoryIds,
    setSelectedInventoryIds,
    selectedInventoryItemIds,
    setSelectedInventoryItemIds,
    classes,
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="xl"
      onClose={handleClose}
      disableEscapeKeyDown={false}
      fullWidth
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        {pickListItem ? (
          <div></div>
        ) : (
          <ListToolbar
            {...props}
            filters={
              <ListFilter
                allInventories={allInventories}
                setInventories={setInventories}
                searchLot={searchLot}
                setSearchLot={setSearchLot}
                isContainerPickUp={isContainerPickUp}
                searchAreaId={searchAreaId}
                setSearchAreaId={setSearchAreaId}
                byLocation={isContainerPickUp ? true : byLocation}
                defaultSort={defaultSort}
                currentSort={inventoryListContext.currentSort}
                setSort={inventoryListContext.setSort}
              />
            }
          />
        )}
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Backdrop open={saving} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {byLocation ? (
          <LocationGrid
            {...commonProps}
            inventories={visibleInventories}
            defaultPerPage={defaultPerPage}
            searchLot={searchLot}
            isContainerPickUp={isContainerPickUp}
            notify={notify}
          />
        ) : (
          <InventoryGrid
            {...commonProps}
            inventoryListContext={inventoryListContext}
          />
        )}
      </DialogContent>
      <DialogActions>
        <div className={classes.textLeft}>
          <Typography>
            {selectedInventoryItemIds.size > 0 ? total : <>&nbsp;</>}
          </Typography>
        </div>
        <div className={classes.actionButtons}>
          <Button
            label="Cancel"
            color="defalut"
            autoFocus
            onClick={handleClose}
          >
            <CancelIcon />
          </Button>
          <Button
            label="Add Selected"
            color="primary"
            onClick={handleAdd}
            disabled={saving}
          >
            <AddAllIcon />
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

const InventoryGrid = ({
  account,
  allInventoriesMap,
  allInventoryItemMap,
  inventoryListContext,
  byLocation,
  setByLocation,
  includeDamaged,
  setIncludeDamaged,
  setDefaultPerPage,
  selectedInventoryIds,
  setSelectedInventoryIds,
  selectedInventoryItemIds,
  setSelectedInventoryItemIds,
  classes,
  ...props
}) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={true}
          />
        );
      })
    : [];
  // Show the account's custom fields by default; users can still hide them
  // per account via the column selection dialog.
  const customFieldSources = accountFields.map(
    (field, index) => `customFields[${index}]`,
  );

  useEffect(() => {
    // onSelect method requires an array of IDs, we convert Set to Array
    const currentSelectedIds = Array.from(selectedInventoryIds);
    inventoryListContext.onSelect(currentSelectedIds);
  }, [selectedInventoryIds]);

  useEffect(() => {
    if (inventoryListContext?.perPage) {
      sessionStorage.setItem(
        `InventorySearchDialog_perPage`,
        inventoryListContext.perPage,
      );
      setDefaultPerPage(Number(inventoryListContext.perPage));
    }
  }, [inventoryListContext?.perPage]);

  const handleSelect = (selectedIds) => {
    const newSelectedInventoryIds = new Set(selectedInventoryIds);
    const newSelectedInventoryItemIds = new Set(selectedInventoryItemIds);
    // select / unselect all inventories
    if (selectedIds.length === 0) {
      // unselect all inventories && inventoryItems
      newSelectedInventoryIds.clear();
      newSelectedInventoryItemIds.clear();
    } else {
      // select all inventories and corresponding inventoryItems
      selectedIds.forEach((selectedId) => {
        newSelectedInventoryIds.add(selectedId);
        const inventory = allInventoriesMap[selectedId];
        // Selecting the parent row must never reach past what the grid shows:
        // with damaged rows filtered out, picking an inventory picks its good
        // items only.
        const inventoryItems = getVisibleInventoryItems(
          inventory,
          includeDamaged,
        );
        inventoryItems.forEach((item) =>
          newSelectedInventoryItemIds.add(item.id),
        );
      });
    }

    setSelectedInventoryIds(newSelectedInventoryIds);
    setSelectedInventoryItemIds(newSelectedInventoryItemIds);
  };

  const handleToggle = (selectedId) => {
    const newSelectedInventoryIds = new Set(selectedInventoryIds);
    const newSelectedInventoryItemIds = new Set(selectedInventoryItemIds);
    const allItemsOfInventory =
      allInventoriesMap[selectedId]?.inventoryItems?.items;

    // toggle inventory selection
    const isInventorySelected = newSelectedInventoryIds.has(selectedId);
    if (isInventorySelected) {
      // inventory selected, unselect it
      newSelectedInventoryIds.delete(selectedId);

      // Unselect against the full item list, not the visible one: a damaged
      // item carried in from an existing pick list is hidden but still
      // selected, and clearing the row must clear it too.
      allItemsOfInventory?.forEach((item) =>
        newSelectedInventoryItemIds.delete(item.id),
      );
    } else {
      // inventory not selected, select it
      newSelectedInventoryIds.add(selectedId);

      // Also select all corresponding inventory items the grid is showing
      getVisibleInventoryItems(
        allInventoriesMap[selectedId],
        includeDamaged,
      ).forEach((item) => newSelectedInventoryItemIds.add(item.id));
    }

    setSelectedInventoryIds(newSelectedInventoryIds);
    setSelectedInventoryItemIds(newSelectedInventoryItemIds);
  };

  return (
    <ListContextProvider value={inventoryListContext}>
      {isSmall ? (
        <CustomizableDatagrid
          label={false}
          listSwitch={
            <ListSwitch
              byLocation={byLocation}
              setByLocation={setByLocation}
              includeDamaged={includeDamaged}
              setIncludeDamaged={setIncludeDamaged}
            />
          }
          hasBulkActions={true}
          onSelect={handleSelect}
          onToggleItem={handleToggle}
          rowClick="toggleSelection"
          storageProp={`InventorySearchDialog_${account?.id}_small`}
          defaultColumns={[
            "hasException",
            "productCode",
            "productName",
            "lotNumber",
            "receivedAt",
            "quantityAvailable",
            ...customFieldSources,
          ]}
        >
          {[
            <FunctionField
              source="hasException"
              label="Exception"
              render={exceptionRenderer}
            />,
            <TextField source="productCode" label="Product Code" />,
            <TextField source="productName" label="Product Name" />,
            <FunctionField source="pcs" label="PPU" render={piecesRenderer} />,
            <TextField source="lotNumber" label="Lot No." />,
            <DateField source="receivedAt" label="Received" />,
            <NumberField source="quantityOnHand" label="On Hand" />,
            <NumberField source="quantityCommitted" label="Committed" />,
            <NumberField source="quantityAvailable" label="Available" />,
            ...customGridFields,
          ]}
        </CustomizableDatagrid>
      ) : (
        <CustomizableDatagrid
          label={false}
          listSwitch={
            <ListSwitch
              byLocation={byLocation}
              setByLocation={setByLocation}
              includeDamaged={includeDamaged}
              setIncludeDamaged={setIncludeDamaged}
            />
          }
          hasBulkActions={true}
          onSelect={handleSelect}
          onToggleItem={handleToggle}
          rowClick="expand"
          expand={
            <InventoryItemPeak
              selectedInventoryIds={selectedInventoryIds}
              setSelectedInventoryIds={setSelectedInventoryIds}
              selectedInventoryItemIds={selectedInventoryItemIds}
              setSelectedInventoryItemIds={setSelectedInventoryItemIds}
              classes={classes}
            />
          }
          storageProp={`InventorySearchDialog_${account?.id}_large`}
          defaultColumns={[
            "hasException",
            "productCode",
            "productName",
            "lotNumber",
            "receivedAt",
            "quantityOnHand",
            "quantityCommitted",
            "quantityAvailable",
            "inventoryUOM",
            ...customFieldSources,
          ]}
        >
          {[
            <FunctionField
              source="hasException"
              label="Exception"
              render={exceptionRenderer}
            />,
            <TextField source="productCode" label="Product Code" />,
            <TextField source="productName" label="Product Name" />,
            <FunctionField source="pcs" label="PPU" render={piecesRenderer} />,
            <FunctionField
              source="totalPcs"
              label="Total PCS"
              headerClassName={classes.textRight}
              sortable={false}
              render={totalPiecesRenderer}
            />,
            <TextField source="lotNumber" label="Lot Number" />,
            <TextField source="poNumber" label="PO Number" />,
            <TextField source="railCarNumber" label="Car/Trl. No." />,
            <DateField source="receivedAt" label="Received" />,
            <FunctionField
              source="turnDays"
              label="Age"
              sortable={false}
              render={ageRenderer}
            />,
            <NumberField source="quantityOnHand" label="On Hand" />,
            <NumberField source="quantityCommitted" label="Committed" />,
            <NumberField source="quantityAvailable" label="Available" />,
            <FunctionField
              source="availablePieces"
              label="Available PCS"
              render={availablePiecesRenderer}
            />,
            <TextField source="inventoryUOM" label="UOM" />,
            <FunctionField
              source="weight"
              label="Weight"
              headerClassName={classes.textRight}
              render={(record) =>
                quantityRenderer(getTotalWeight(record), record.weightUOM)
              }
            />,
            ...customGridFields,
          ]}
        </CustomizableDatagrid>
      )}
      <Pagination {...props} {...inventoryListContext} />
    </ListContextProvider>
  );
};

const LocationGrid = ({
  account,
  inventories,
  allInventoriesMap,
  allInventoryItemMap,
  byLocation,
  setByLocation,
  includeDamaged,
  setIncludeDamaged,
  defaultPerPage,
  setDefaultPerPage,
  selectedInventoryIds,
  setSelectedInventoryIds,
  selectedInventoryItemIds,
  setSelectedInventoryItemIds,
  searchLot,
  isContainerPickUp,
  classes,
  notify,
  ...props
}) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        return (
          <CustomField
            source={`inventory.customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={true}
          />
        );
      })
    : [];
  // Show the account's custom fields by default; users can still hide them
  // per account via the column selection dialog.
  const customFieldSources = accountFields.map(
    (field, index) => `inventory.customFields[${index}]`,
  );

  const defaultSort = { field: "inventory.productName", order: "ASC" };
  const secondarySort =
    searchLot === LotType.PERPETUAL
      ? { field: "inventory.receivedAt", order: "ASC" }
      : searchLot === LotType.CARLOT
        ? { field: "inventory.lotNumber", order: "ASC" }
        : null;

  // Memoize inventoryItems
  const inventoryItems = useMemo(() => {
    // generate the array of all inventoryItems
    const items = inventories.reduce((acc, inventory) => {
      // inventory items filtered in getAllInventories
      return acc.concat(inventory.inventoryItems.items);
    }, []);

    // sort by picking order
    const sortedItems = sortByLocation(items, true);
    return sortedItems;
  }, [inventories]); // Depend on inventories array

  // const listContext = useList({
  //   ...props,
  //   data: inventoryItems,
  //   ids: inventoryItems.map(({ id }) => id),
  //   loaded: true,
  //   filter: fixedFilter,
  //   sort: defaultSort,
  //   perPage: defaultPerPage,
  //   basePath: "/inventoryItems",
  //   resource: "inventoryItems",
  // });
  // Memoized for the same reason as the ids in the inventory list above.
  const inventoryItemIds = useMemo(
    () => inventoryItems.map(({ id }) => id),
    [inventoryItems],
  );

  const listContext = useListWithSecondarySort({
    ...props,
    data: inventoryItems,
    ids: inventoryItemIds,
    loaded: true,
    sort: defaultSort,
    secondarySort: secondarySort,
    perPage: defaultPerPage,
    basePath: "/inventoryItems",
    resource: "inventoryItems",
    notify,
  });

  useEffect(() => {
    // onSelect method requires an array of IDs, we convert Set to Array
    const currentSelectedIds = Array.from(selectedInventoryItemIds);
    listContext.onSelect(currentSelectedIds);
  }, [selectedInventoryItemIds]);

  useEffect(() => {
    if (listContext?.perPage) {
      sessionStorage.setItem(
        `InventorySearchDialog_perPage`,
        listContext.perPage,
      );
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  const handleSelect = (selectedIds) => {
    const newSelectedInventoryItemIds = new Set(selectedInventoryItemIds);
    const newSelectedInventoryIds = new Set(selectedInventoryIds);

    // Identify items on the current page to be potentially deselected
    const currentPageItemIds = listContext.ids;

    // Remove current page items from the selection if they are not in selectedIds
    currentPageItemIds.forEach((id) => {
      if (!selectedIds.includes(id)) {
        newSelectedInventoryItemIds.delete(id);
        // If removing an item causes its parent inventory to have no selected items, also remove the inventory
        const inventoryId = allInventoryItemMap[id].inventoryId;
        const allItemsOfInventory =
          allInventoriesMap[inventoryId]?.inventoryItems?.items.map(
            (item) => item.id,
          ) || [];
        const anySelected = allItemsOfInventory.some((itemId) =>
          newSelectedInventoryItemIds.has(itemId),
        );
        if (!anySelected) {
          newSelectedInventoryIds.delete(inventoryId);
        }
      }
    });

    // For items that are selected on the current page, ensure they and their parent inventories are selected
    selectedIds.forEach((id) => {
      newSelectedInventoryItemIds.add(id);
      const inventoryId = allInventoryItemMap[id].inventoryId;
      newSelectedInventoryIds.add(inventoryId);
    });

    // Update the component state with the new selections
    setSelectedInventoryIds(newSelectedInventoryIds);
    setSelectedInventoryItemIds(newSelectedInventoryItemIds);
  };

  const handleToggle = (selectedId) => {
    const newSelectedInventoryItemIds = new Set(selectedInventoryItemIds);
    const newSelectedInventoryIds = new Set(selectedInventoryIds);
    const inventoryItem = allInventoryItemMap[selectedId];
    const inventoryId = inventoryItem.inventoryId;
    const allItemsOfInventory = allInventoriesMap[
      inventoryId
    ]?.inventoryItems?.items.map((item) => item.id);

    // toggle inventoryItem selection state
    const isInventoryItemSelected = newSelectedInventoryItemIds.has(selectedId);
    if (isInventoryItemSelected) {
      // unSelect inventoryItem
      newSelectedInventoryItemIds.delete(selectedId);
      const anySelected = allItemsOfInventory?.some((id) =>
        newSelectedInventoryItemIds.has(id),
      );

      if (!anySelected) {
        // all inventoryItems of inventory unselected, unselect inventory
        newSelectedInventoryIds.delete(inventoryId);
      }
    } else {
      // check / select inventoryItem
      newSelectedInventoryItemIds.add(selectedId);

      const isFirstSelected =
        allItemsOfInventory.filter((id) => newSelectedInventoryItemIds.has(id))
          .length === 1;
      if (isFirstSelected) {
        // if first inventoryItems of inventory is selected, select inventory
        newSelectedInventoryIds.add(inventoryId);
      }
    }

    setSelectedInventoryItemIds(newSelectedInventoryItemIds);
    setSelectedInventoryIds(newSelectedInventoryIds);
  };

  return (
    <ListContextProvider value={listContext}>
      {isSmall ? (
        <CustomizableDatagrid
          label={false}
          listSwitch={
            <ListSwitch
              byLocation={byLocation}
              setByLocation={setByLocation}
              includeDamaged={includeDamaged}
              setIncludeDamaged={setIncludeDamaged}
              isContainerPickUp={isContainerPickUp}
            />
          }
          hasBulkActions={true}
          onSelect={handleSelect}
          onToggleItem={handleToggle}
          rowClick="toggleSelection"
          storageProp={`InventoryItemSearchDialog_${account?.id}_small`}
          defaultColumns={[
            "inventory.hasException",
            "locationFullName",
            "inventory.productName",
            "quantityAvailable",
            ...customFieldSources,
          ]}
        >
          {[
            <FunctionField
              source="inventory.hasException"
              label="Exception"
              render={exceptionRenderer}
            />,
            <TextField source="locationFullName" label="Location" />,
            <TextField source="inventory.productName" label="Product Name" />,
            <FunctionField
              source="pcs"
              label="Pieces"
              render={piecesRenderer}
            />,
            <TextField source="inventory.lotNumber" label="Lot No." />,
            <NumberField source="quantityOnHand" label="On Hand" />,
            <NumberField source="quantityCommitted" label="Committed" />,
            <FunctionField
              source="quantityAvailable"
              label="Available"
              headerClassName={classes.textRight}
              sortable={false}
              render={(record) =>
                trimDecimal(record.quantityOnHand - record.quantityCommitted, 3)
              }
            />,

            ...customGridFields,
          ]}
        </CustomizableDatagrid>
      ) : (
        <CustomizableDatagrid
          label={false}
          listSwitch={
            <ListSwitch
              byLocation={byLocation}
              setByLocation={setByLocation}
              includeDamaged={includeDamaged}
              setIncludeDamaged={setIncludeDamaged}
              isContainerPickUp={isContainerPickUp}
            />
          }
          hasBulkActions={true}
          onSelect={handleSelect}
          onToggleItem={handleToggle}
          rowClick="toggleSelection"
          storageProp={`InventoryItemSearchDialog_${account?.id}_large`}
          defaultColumns={[
            "inventory.hasException",
            "locationFullName",
            "inventory.productName",
            "inventory.lotNumber",
            "inventory.receivedAt",
            "quantityOnHand",
            "quantityCommitted",
            "quantityAvailable",
            "inventory.inventoryUOM",
            ...customFieldSources,
          ]}
        >
          {[
            <FunctionField
              source="inventory.hasException"
              label="Exception"
              render={exceptionRenderer}
            />,
            <TextField source="locationFullName" label="Location" />,
            <TextField source="inventory.productCode" label="Product Code" />,
            <TextField source="inventory.productName" label="Product Name" />,
            <FunctionField
              source="pcs"
              label="Pieces"
              render={piecesRenderer}
            />,
            <TextField source="inventory.lotNumber" label="Lot Number" />,
            <TextField source="inventory.poNumber" label="PO Number" />,
            <TextField source="inventory.railCarNumber" label="Car/Trl. No." />,
            <DateField source="inventory.receivedAt" label="Received" />,
            <FunctionField
              source="inventory.turnDays"
              label="Age"
              sortable={false}
              render={ageRenderer}
            />,
            <NumberField source="quantityOnHand" label="On Hand" />,
            <NumberField source="quantityCommitted" label="Committed" />,
            <FunctionField
              source="quantityAvailable"
              label="Available"
              headerClassName={classes.textRight}
              sortable={false}
              render={(record) =>
                trimDecimal(record.quantityOnHand - record.quantityCommitted, 3)
              }
            />,
            <TextField source="inventory.inventoryUOM" label="UOM" />,
            <FunctionField
              source="inventory.weight"
              label="Weight"
              headerClassName={classes.textRight}
              render={(record) =>
                quantityRenderer(
                  getTotalWeight(record),
                  record.inventory.weightUOM,
                )
              }
            />,
            ...customGridFields,
          ]}
        </CustomizableDatagrid>
      )}
      <Pagination {...props} {...listContext} />
    </ListContextProvider>
  );
};

const InventoryItemPeak = ({
  record,
  selectedInventoryIds,
  setSelectedInventoryIds,
  selectedInventoryItemIds,
  setSelectedInventoryItemIds,
  classes,
  ...props
}) => {
  const defaultSort = { field: "locationFullName", order: "ASC" };
  const fixedFilter = {};
  const defaultPerPage = 100; // default # of @connection items brought back

  // Memoize inventoryItems
  const inventoryItems = useMemo(() => {
    // sort by picking order. items pre-filtered in getAllInventories
    const sortedItems = sortByLocation(record.inventoryItems.items, true);
    return sortedItems;
  }, [record]);

  // Memoized for the same reason as the ids in the inventory list above.
  const inventoryItemIds = useMemo(
    () => inventoryItems.map(({ id }) => id),
    [inventoryItems],
  );

  const listContext = useList({
    ...props,
    data: inventoryItems,
    ids: inventoryItemIds,
    loaded: true,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/inventoryItems",
    resource: "inventoryItems",
  });

  useEffect(() => {
    const allItemsOfInventory = record.inventoryItems.items;
    // Find items from allItemsOfInventory that are in selectedInventoryItemIds
    const selectedItemIds = allItemsOfInventory
      .map((item) => item.id) // Extract IDs from all items
      .filter((id) => selectedInventoryItemIds.has(id)); // Keep only IDs that are selected

    listContext.onSelect(selectedItemIds);
  }, [selectedInventoryItemIds]);

  const handleSelect = (selectedIds) => {
    const newSelectedInventoryIds = new Set(selectedInventoryIds);
    const newSelectedInventoryItemIds = new Set(selectedInventoryItemIds);
    // select / unselect all inventoryItems
    if (selectedIds.length === 0) {
      // unselect all inventoryItems
      const allItemsOfInventory = record.inventoryItems.items.map(
        (item) => item.id,
      );
      allItemsOfInventory.forEach((itemId) =>
        newSelectedInventoryItemIds.delete(itemId),
      );

      // unselect parent inventory
      newSelectedInventoryIds.delete(record.id);
    } else {
      // select all inventoryItems
      selectedIds.forEach((selectedId) =>
        newSelectedInventoryItemIds.add(selectedId),
      );

      // select parent inventory
      newSelectedInventoryIds.add(record.id);
    }

    setSelectedInventoryIds(newSelectedInventoryIds);
    setSelectedInventoryItemIds(newSelectedInventoryItemIds);
  };

  const handleToggle = (selectedId) => {
    const newSelectedInventoryIds = new Set(selectedInventoryIds);
    const newSelectedInventoryItemIds = new Set(selectedInventoryItemIds);
    // Toggle the selection state of the inventory item
    const isInventoryItemSelected = newSelectedInventoryItemIds.has(selectedId);
    if (isInventoryItemSelected) newSelectedInventoryItemIds.delete(selectedId);
    else newSelectedInventoryItemIds.add(selectedId);

    // Check the selection state of all items for the current inventory
    const allItemsOfInventory = record.inventoryItems.items.map(
      (item) => item.id,
    );
    const anyItemSelected = allItemsOfInventory.some((id) =>
      newSelectedInventoryItemIds.has(id),
    );

    // toggle parent inventory selection state
    if (anyItemSelected) {
      // If any item is selected, ensure the inventory is marked as selected
      newSelectedInventoryIds.add(record.id);
    } else {
      // If no items are selected, remove the inventory from the selection
      newSelectedInventoryIds.delete(record.id);
    }

    setSelectedInventoryIds(newSelectedInventoryIds);
    setSelectedInventoryItemIds(newSelectedInventoryItemIds);
  };

  return (
    <Paper variant="outlined" className={classes.paper}>
      <ListContextProvider value={listContext}>
        <Datagrid
          hasBulkActions={true}
          onSelect={handleSelect}
          onToggleItem={handleToggle}
          rowClick="toggleSelection"
        >
          <FunctionField
            source="hasException"
            label=""
            render={exceptionRenderer}
          />
          ,
          <TextField source="locationFullName" label="Location" />,
          <NumberField source="quantityOnHand" label="On Hand" />,
          <NumberField source="quantityCommitted" label="Committed" />,
          <FunctionField
            source="quantityAvailable"
            label="Available"
            headerClassName={classes.textRight}
            sortable={false}
            render={(record) =>
              trimDecimal(record.quantityOnHand - record.quantityCommitted, 3)
            }
          />
          ,
          <TextField source="inventory.inventoryUOM" label="UOM" />,
          <FunctionField
            source="inventory.weight"
            label="Weight"
            headerClassName={classes.textRight}
            render={(record) =>
              quantityRenderer(
                getTotalWeight(record),
                record.inventory.weightUOM,
              )
            }
          />
          ,
        </Datagrid>
      </ListContextProvider>
    </Paper>
  );
};
